window.StockCubeCore = window.StockCubeCore || {};

window.StockCubeCore.setupChartDataListeners = function () {
    this.chartDataFetchStartedListener = (event) => {
        const { ticker, dataInterval } = event.detail;
        if (__LOG_INFO__) AppLog.info(`[Stock Visualizer] Chart started fetching data for ${ticker} (${dataInterval})`);
        this.isChartDataReady = false;
        this.waitingForChartData = true;
    };

    this.chartDataFetchCompleteListener = (event) => {
        const { dateRange } = event.detail;
        if (dateRange) {
            if (__LOG_INFO__) AppLog.info(`[Stock Visualizer] Chart data range: ${dateRange.start} to ${dateRange.end}`);
        }
        this.isChartDataReady = true;
        this.waitingForChartData = false;
        this.chartDataRange = dateRange;

        if (this.pendingAnimationData) {
            this.startAnimationOrRenderFinal(this.pendingAnimationData.result, this.pendingAnimationData.fetchId);
            this.pendingAnimationData = null;
        }
    };

    this.chartDataFetchErrorListener = (event) => {
        const { ticker, error } = event.detail;
        if (__LOG_ERROR__) AppLog.error(`[Stock Visualizer] Chart data fetch error for ${ticker}: ${error}`);
        this.isChartDataReady = false;
        this.waitingForChartData = false;
    };

    this.scene.addEventListener('chart-data-fetch-started', this.chartDataFetchStartedListener);
    this.scene.addEventListener('chart-data-fetch-complete', this.chartDataFetchCompleteListener);
    this.scene.addEventListener('chart-data-fetch-error', this.chartDataFetchErrorListener);
};

window.StockCubeCore.fetchStockData = async function () {
    const fetchId = ++this.currentFetchId;
    const container = this.el;
    const dataSource = this.data.dataSource;
    const sectorIds = this.data.sectorIds.map(Number);

    if (__LOG_INFO__) AppLog.info(`fetchStockData started, fetchId: ${fetchId}, dataSource: ${dataSource}`);

    const isInitialLoad = !this.isInitialized;
    if (!isInitialLoad && window.PreloaderManager && typeof window.PreloaderManager.show === 'function') {
        if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Showing main preloader for interval change');
        window.PreloaderManager.show();
    }

    const hidePreloader = () => {
        if (!isInitialLoad && window.PreloaderManager && typeof window.PreloaderManager.hide === 'function') {
            setTimeout(() => window.PreloaderManager.hide(), 300);
        }
    };

    try {

        if (!this.pvtechApiManager) {
            throw new Error('PVTechAPIManager is not available');
        }

        const result = await this.fetchDataBySource(dataSource, sectorIds);

        if (fetchId !== this.currentFetchId) {
            if (__LOG_INFO__) AppLog.info(`Fetch ${fetchId} outdated, skipping`);
            hidePreloader();
            return;
        }

        if (result === null) {
            this.showError(this.getErrorMessage(dataSource));
            hidePreloader();
            return;
        }

        if (dataSource === 'watchlist-reports') {
            const state = window.WatchlistApp && window.WatchlistApp.State;
            let watchlistName = 'Watchlist Reports';
            if (state && state.allWatchlists && state.currentWatchlistId) {
                const wl = state.allWatchlists.find(w => String(w.id) === String(state.currentWatchlistId));
                if (wl && wl.name) watchlistName = wl.name;
            } else if (state && state.allWatchlists && state.allWatchlists.length > 0) {
                watchlistName = state.allWatchlists[0].name || watchlistName;
            }
            const bannerEl = document.getElementById('stock-banner');
            if (bannerEl) bannerEl.setAttribute('value', watchlistName);
            this.el.setAttribute('stock-cube-visualizer', 'banner', watchlistName);
        }

        this.processSuccessfulResult(result, fetchId);
        this.isInitialized = true;
        hidePreloader();

        if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Emitting stock-data-loaded event');
        this.el.sceneEl.emit('stock-data-loaded', { dataSource, fetchId });
        document.dispatchEvent(new CustomEvent('stock-data-loaded', { detail: { dataSource, fetchId } }));

    } catch (error) {
        if (__LOG_ERROR__) AppLog.error('Error fetching stock data:', error);
        this.showError(`Failed to fetch stock data: ${error.message}`);
        hidePreloader();

        if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Emitting stock-data-loaded event (error case)');
        this.el.sceneEl.emit('stock-data-loaded', { dataSource, fetchId, error: true });
        document.dispatchEvent(new CustomEvent('stock-data-loaded', { detail: { dataSource, fetchId, error: true } }));
    }
};

window.StockCubeCore.fetchDataBySource = async function (dataSource, sectorIds) {
    const commonParams = {
        date: new Date().toLocaleString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).split('/').reverse().join('-'),
        dataInterval: this.data.interval
    };

    const MMAP_TOP_N = 200;

    const dataSourceMap = {
        'top-gainers': () => this.pvtechApiManager.fetchTopGainersByPrice({ ...commonParams, top_n: 50 }),
        'top-losers': () => this.pvtechApiManager.fetchTopLosersByPrice({ ...commonParams, top_n: 50 }),
        'top-gainers-sec': () => this.pvtechApiManager.fetchTopGainersByPriceSegment({ ...commonParams, top_n: 50 }),
        'top-losers-sec': () => this.pvtechApiManager.fetchTopLosersByPriceSegment({ ...commonParams, top_n: 50 }),
        'top-vol': () => this.pvtechApiManager.fetchTopVolumeByShares({ ...commonParams, sector_ids: sectorIds, top_n: 50 }),
        'top-vol-sec': () => this.pvtechApiManager.fetchTopVolumeBySharesSegment({ ...commonParams, sector_ids: sectorIds, top_n: this.data.topN }),
        'top-dollar-vol': () => this.pvtechApiManager.fetchTopDollarVolumeOverall({ ...commonParams, top_n: 50, sector_ids: sectorIds }),
        'top-dollar-vol-sec': () => this.pvtechApiManager.fetchTopDollarVolumeBySegment({ ...commonParams, top_n: this.data.topN, sector_ids: sectorIds }),
        'mmap': () => this.pvtechApiManager.fetchMarketMap({ ...commonParams, top_n: MMAP_TOP_N, sector_ids: sectorIds }),
        'sip': () => this.pvtechApiManager.fetchShortInterestOverall({ ...commonParams, top_n: -1, sector_ids: sectorIds }),
        'sip-sec': () => this.pvtechApiManager.fetchShortInterestBySegment({ ...commonParams, top_n: -1, sector_ids: sectorIds }),
        'dividend': () => this.pvtechApiManager.fetchDividendOverall({ ...commonParams, top_n: -1, sector_ids: sectorIds }),
        'dividend-sec': () => this.pvtechApiManager.fetchDividendBySegment({ ...commonParams, top_n: -1, sector_ids: sectorIds }),
        'watchlist-reports': async () => {
            if (this.injectedDataCache) {
                if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Using event-based injectedDataCache');
                const data = this.injectedDataCache;
                this.injectedDataCache = null;
                return data;
            }

            if (this.data.injectedData) {
                try {
                    if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Using injectedData for watchlist-reports');
                    const parsedData = JSON.parse(this.data.injectedData);
                    if (parsedData && (parsedData.stockInfo || parsedData.intervals)) {
                        return parsedData;
                    }
                } catch (e) {
                    if (__LOG_WARN__) AppLog.warn('[StockCubeVisualizer] Failed to parse injectedData', e);
                }
            }

            const allWatchlists = window.WatchlistApp && window.WatchlistApp.State && window.WatchlistApp.State.allWatchlists;
            if (allWatchlists) {
                const current = allWatchlists.find(w => w.id === this.data.watchlistId);
                if (current && (!current.tickers || current.tickers.length === 0)) {
                    if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] Empty watchlist, returning empty result');
                    return { stockInfo: [], intervals: {} };
                }
            }

            try {
                const auth = window.WatchlistAPIManager && window.WatchlistAPIManager.instance;
                if (!auth) {
                    if (__LOG_ERROR__) AppLog.error('WatchlistAPIManager not available');
                    return null;
                }

                const token = await auth.getAuthToken();
                const session = window.currentSession;
                const userId = session?.user?.id;

                if (!token || !userId) {
                    if (__LOG_WARN__) AppLog.warn('User not authorized for watchlist (No token or userId)');
                    return null;
                }

                const watchlistsData = await auth.getWatchlists();
                let watchlists = [];
                if (watchlistsData) {
                    watchlists = Array.isArray(watchlistsData.watchlists) ? watchlistsData.watchlists : (Array.isArray(watchlistsData) ? watchlistsData : []);
                }

                if (watchlists.length === 0) {
                    if (__LOG_WARN__) AppLog.warn('No watchlists found for user');
                    if (this.el.sceneEl) {
                        this.el.sceneEl.emit('watchlist-main-empty-state', { active: true, action: 'create' });
                    }
                    return { emptyState: 'create' };
                }

                window.WatchlistApp = window.WatchlistApp || {};
                window.WatchlistApp.State = window.WatchlistApp.State || {};
                window.WatchlistApp.State.allWatchlists = watchlists;

                let targetWatchlist = watchlists[0];
                if (window.WatchlistApp.State.currentWatchlistId) {
                    const foundWl = watchlists.find(w => String(w.id) === String(window.WatchlistApp.State.currentWatchlistId));
                    if (foundWl) targetWatchlist = foundWl;
                }
                const watchlistId = targetWatchlist.id;
                window.WatchlistApp.State.currentWatchlistId = watchlistId;

                if (__LOG_INFO__) AppLog.info(`Fetching watchlist reports for watchlistId: ${watchlistId}`);
                const reportData = await auth.getWatchlistReports(this.data.interval, userId, watchlistId);
                if (__LOG_INFO__) AppLog.info('[StockCubeVisualizer] watchlist-reports raw data:', reportData);

                let processedData = { intervals: {}, stockInfo: {} };
                if (window.WatchlistDataProcessor) {
                    const holdings = targetWatchlist.holdings || {};
                    const tickers = targetWatchlist.tickers || [];

                    if (Object.keys(holdings).length === 0 && tickers.length === 0) {
                        if (__LOG_WARN__) AppLog.warn('Current watchlist is empty');
                        if (this.el.sceneEl) {
                            this.el.sceneEl.emit('watchlist-main-empty-state', { active: true, action: 'add' });
                        }
                        return { emptyState: 'add' };
                    }

                    const tickerInfo = reportData.response?.ticker_info || {};
                    processedData = window.WatchlistDataProcessor.processWatchlistReport(reportData, this.data.interval, holdings, tickerInfo);

                    window.WatchlistApp.State.currentWatchlistData = processedData.stockInfo;

                    if (window.WatchlistApp.Summary && window.WatchlistApp.Summary.calculateSummary) {
                        window.WatchlistApp.State.summary = window.WatchlistApp.Summary.calculateSummary(processedData, holdings, this.data.interval);
                    }
                } else {
                    if (__LOG_ERROR__) AppLog.error('WatchlistDataProcessor not found!');
                }

                return processedData;
            } catch (e) {
                if (__LOG_ERROR__) AppLog.error('Error fetching watchlist reports:', e);
                return null;
            }
        }
    };

    const fetchFunction = dataSourceMap[dataSource];
    if (!fetchFunction) {
        throw new Error(`Unknown data source: ${dataSource}`);
    }

    return await fetchFunction();
};

window.StockCubeCore.getCachedStockInfoMap = function () {
    const stockInfoMap = new Map();
    if (this.stockInfo) {
        if (Array.isArray(this.stockInfo)) {
            this.stockInfo.forEach(stock => {
                if (stock && stock.ticker) stockInfoMap.set(stock.ticker, stock);
            });
        } else {
            Object.values(this.stockInfo).forEach(stock => {
                if (stock && stock.ticker) stockInfoMap.set(stock.ticker, stock);
            });
        }
    }
    return stockInfoMap;
};

window.StockCubeCore.processSuccessfulResult = function (result, fetchId) {
    if (result && result.emptyState) {
        this.hideError();
        this.stockInfo = {};
        this.currentData = null;
        return;
    }

    if (this.data.dataSource === 'watchlist-reports') {
        if (this.el.sceneEl) {
            this.el.sceneEl.emit('watchlist-main-empty-state', { active: false });
        }
    }

    this._cachedGlobalMax = null;
    this.maxHeightValue = undefined;
    this.hasNegativeGlobal = false;

    const { stockInfo } = result;
    this.stockInfo = stockInfo;

    if (this.data.excludedTickers && this.data.excludedTickers.length > 0) {
        if (Array.isArray(this.stockInfo)) {
            this.stockInfo = this.stockInfo.filter(s => !this.data.excludedTickers.includes(s.ticker));
        }
    }

    this.normalizeIntervals(result);

    if (!result.metadata) result.metadata = {};
    let previousCloseDate = result.metadata.previous_close_date || '';

    if (!previousCloseDate && result.stockInfo && typeof result.stockInfo === 'object') {
        const firstStock = Object.values(result.stockInfo).find(Boolean);
        previousCloseDate = firstStock?.tickerSummary?.previous_close_date || '';
    }

    if (!previousCloseDate && result.intervals && Object.keys(result.intervals).length > 0) {
        const timestamps = Object.keys(result.intervals).sort();
        if (timestamps.length > 0) previousCloseDate = timestamps[0].split(' ')[0];
    }
    result.metadata.previous_close_date = previousCloseDate;

    this.currentData = result;

    const allTimestamps = Object.keys(result.intervals || {}).sort();
    const dataIntervalCache = typeof getCacheItem === 'function' ? getCacheItem('data-interval') : null;
    const interval = (dataIntervalCache ? dataIntervalCache.toLowerCase() : null) || this.data.interval || '1d';

    if (this.data.timelineEntity) {
        this.data.timelineEntity.setAttribute('timeline-control', {
            dataSource: this.data.dataSource,
            interval: interval,
            previousCloseDate: previousCloseDate,
            timestamps: allTimestamps
        });
    }

    if (this.data.dataSource !== 'mmap') {
        const refStocks = allTimestamps.length ? (result.intervals[allTimestamps[allTimestamps.length - 1]] || []) : [];
        const dsConfig = this.resolveDataSourceConfig(
            this.data.dataSource,
            refStocks,
            this.formatNumber ? this.formatNumber.bind(this) : (v => v)
        );
        const heightValueFn = dsConfig && dsConfig.heightValueFn;
        if (typeof heightValueFn === 'function') {
            const globalData = this.calculateGlobalMaxHeightValue(heightValueFn);
            this._cachedGlobalMax = globalData;
            this.maxHeightValue = globalData.max;
            if (globalData.hasNegative) this.hasNegativeGlobal = true;
        }
    }

    this.hideError();
    this.fireworksController?.clearFireworkIntervals();
    if (window.Fireworks) window.Fireworks.emergencyStopFireworks();
    if (this.stockBars) this.stockBars.clear();

    if (this.data.dataSource === 'watchlist-reports') {
        const stockCount = Array.isArray(result.stockInfo)
            ? result.stockInfo.length
            : Object.keys(result.stockInfo || {}).length;

        if (__LOG_INFO__) AppLog.info('[Stock Visualizer] Emitting watchlist-data-loaded event', result, stockCount);
        this.el.dispatchEvent(new CustomEvent('watchlist-data-loaded', {
            detail: { stockInfo: result.stockInfo, totalStocks: stockCount },
            bubbles: true
        }));
    }

    if (this.data.dataSource === 'mmap') {
        if (window.Fireworks) {
            window.Fireworks.emergencyStopFireworks();
        }
        clearInterval(this.autoLaunchInterval);
        if (!this.mmapViewMode) {
            this.mmapViewMode = (typeof getCacheItem === 'function' ? getCacheItem('mmap_view_mode') : null) || '2d';
        }
        this.calculateBehavioralFlags();
        if (!this.mmapPrimaryLabelConfig) {
            this.mmapPrimaryLabelConfig = {
                fontSize: 2,
                scale: "0.1 0.1 0.1",
                outlineWidth: "15%",
                outlineBlur: "20%",
                outlineColor: "#000000"
            };
        }
        if (this.mmapViewMode === '3d') {
            const isDesktopPlatformMmap = this.platformConfig && this.platformConfig.platform === 'desktop';
            const isVRPlatformMmap = this.platformConfig && this.platformConfig.platform === 'vr';
            const wrapper = document.querySelector('#stock-3d-cube-wrapper');
            if (wrapper) {
                wrapper.setAttribute('rotation', isVRPlatformMmap ? '30 45 0' : '30 0 0');
            }
            if (isDesktopPlatformMmap) {
                this.el.setAttribute('scale', '0.95 0.95 0.95');
            }
            this.isFirstRender = true;
            window.MarketMapSharedRendering.renderMarketMap(this, result);
            this.isFirstAnimationPlay = false;

            if (allTimestamps.length) {
                if (this.data.timelineEntity) {
                    const finalTime = allTimestamps[allTimestamps.length - 1];
                    this.data.timelineEntity.emit('timeline-update', {
                        timestamp: finalTime,
                        index: allTimestamps.length - 1,
                        total: allTimestamps.length
                    }, true);
                }
                this.currentTimestamp = allTimestamps[allTimestamps.length - 1];
            }
        } else {
            this.renderFinalFrame(result);
        }
        return;
    }



    const isDesktopPlatformDL = this.platformConfig && this.platformConfig.platform === 'desktop';
    if (isDesktopPlatformDL) {
        try {
            const wrapper = document.querySelector('#stock-3d-cube-wrapper');
            if (wrapper) {
                if (this.data.dataSource === 'top-dollar-vol' || this.data.dataSource === 'top-dollar-vol-sec') {
                    wrapper.setAttribute('position', '0 0.250 .5');
                    if (wrapper.object3D) wrapper.object3D.position.set(0, 0.250, 0.5);
                    setTimeout(() => {
                        const wrapperCheck = document.querySelector('#stock-3d-cube-wrapper');
                        if (wrapperCheck && (this.data.dataSource === 'top-dollar-vol' || this.data.dataSource === 'top-dollar-vol-sec')) {
                            wrapperCheck.setAttribute('position', '0 0.250 .5');
                            if (wrapperCheck.object3D) wrapperCheck.object3D.position.set(0, 0.250, 0.5);
                        }
                    }, 100);
                } else if (this.mmapWrapperOriginalPosition) {
                    this.mmapWrapperOriginalPosition = null;
                }
                if (this.data.dataSource !== 'mmap') {
                    wrapper.setAttribute('scale', '1 1 1');
                    if (wrapper.object3D) wrapper.object3D.scale.set(1, 1, 1);
                }
            }
            if (this.data.dataSource !== 'mmap' && this.mmapContainerOriginalPosition) {
                this.mmapContainerOriginalPosition = null;
                this.mmap2DPosition = null;
            }
            // If we left mmap while in 3D mode the camera was moved outward and never
            // restored (that only happens in switchTo2D). Restore it now so that the
            // next visit to mmap (2D or 3D) calculates positions from the correct Z.
            if (this.data.dataSource !== 'mmap' && this._mmapCameraZBefore3D !== undefined) {
                try {
                    const cam = document.getElementById('camera');
                    if (cam) {
                        const camPos = cam.getAttribute('position') || { x: 0, y: 2.75, z: 2 };
                        const cx = typeof camPos === 'string' ? parseFloat(camPos.split(' ')[0]) : (camPos.x || 0);
                        const cy = typeof camPos === 'string' ? parseFloat(camPos.split(' ')[1]) : (camPos.y || 2.75);
                        cam.setAttribute('position', `${cx} ${cy} ${this._mmapCameraZBefore3D}`);
                        if (cam.object3D) cam.object3D.position.set(cx, cy, this._mmapCameraZBefore3D);
                    }
                } catch (e) { /* no-op */ }
                this._mmapCameraZBefore3D = undefined;
            }
        } catch (e) { /* no-op */ }
    }

    const shouldWaitForChart = this.waitingForChartData;
    if (shouldWaitForChart && !this.isChartDataReady) {
        if (__LOG_INFO__) AppLog.info(`[Stock Visualizer] Chart data fetch in progress, storing animation data for later`);
        this.pendingAnimationData = { result, fetchId };
    } else {
        if (__LOG_INFO__) AppLog.info(`[Stock Visualizer] Chart data ready or not waiting, deciding render mode`);
        this.startAnimationOrRenderFinal(result, fetchId);
    }
};
